import React, { useCallback, useMemo, useRef, useState } from "react";
import { css, useTheme } from "@emotion/react";
import { useNavigate } from 'react-router-dom';
import pluralize from "pluralize";

import { useEventRegistrationContext } from "core/contexts/EventRegistrationContext";
import useBreakpoint from "core/hooks/useBreakpoint";
import useEventRegistrationForm from "core/hooks/useEventRegistrationForm";
import { up } from "core/includes/routes";
import ContentCombine from "core/Components/ContentCombine";
import Collapse from "core/Components/Collapse";
import RegistrationForm from "pages/Components/RegistrationForm";
import FormButton from "core/Components/Form/FormButton";
import Form from "core/Components/Form/Form";
import EventFormContainer from "pages/Components/EventRegistration/EventFormContainer";
import EventRegistrationFormActions from "pages/Components/EventRegistration/EventRegistrationFormActions";
import EventDetail from "pages/Components/EventRegistration/EventDetail";
import AddedToCartDialog from "pages/Components/AddedToCartDialog";
import useCart from "pages/Components/CartPage/hooks/useCart";
import { ReactComponent as Chevron } from "images/icons/thick-chevron.svg";
import { isEmpty } from "lodash";
import { useAppState } from "core/contexts/AppContext";
import ConfirmationModal from "pages/Components/CartAndCheckout/components/ConfirmationModal";

type Props = {
    eventPage: EventPage,
    existingRegistrationData: any,
    userCode: string|null,
};

const DetailConfirmation: React.FC<Props> = ({
    eventPage,
    existingRegistrationData,
    userCode,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { breakpoint } = useBreakpoint();
    const [appState] = useAppState();

    const [isAddedToCartDialogOpen, setIsAddedToCartDialogOpen] = useState(false);
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const [showErrorPopup, setShowErrorPopup] = useState(false);

    const containerRefs: any = useRef({});

    const registrantsData = existingRegistrationData?.registrants;
    const registrationPageData: EventRegistrationPage = eventPage.registrationPage;
    const formFields = registrationPageData.formFields;
    const collapseIdPrefix = `registration-${eventPage.id}-registrant-`;

    const { hasRegistrants } = useCart();

    const {
        toggleExpandedId,
        getIsIdExpanded,
        setIsEditing,
        setSelectedRegistrantIndex,
        setExpandedIds,
    } = useEventRegistrationContext();

    const ignoreFormFieldCallback = useCallback((f: FormField) => !f.isOnConfirmationPage, []);

    const {
        registrationFields: fields,
        registrationErrors: errors,
        formFieldsForRender,
        addToCart,
        onFieldChange,
        onSubmit,
        isValid,
    } = useEventRegistrationForm({
        formFields,
        registrationPageData,
        userCode,
        ignoreFormFieldCallback,
        editExistingWithId: existingRegistrationData.id,
    });

    const isAlreadyInCart = useMemo(() => (
        hasRegistrants(parseInt(eventPage.id), userCode)
    ), [hasRegistrants, eventPage.id, userCode]);

    const onConfirm = useCallback(() => {
        if (!isEmpty(appState.cart) &&
            (eventPage.isOnline ?
                appState.ipCountry?.code !== appState.countryCode?.code :
                appState.ipCountry?.code !== appState.cart.countryCode)
        ) {
            setShowErrorPopup(true);
            return null;
        }

        setIsButtonLoading(true);

        onSubmit()
            .then(({ data }: { data: any }) => {
                const addToCartMutation = addToCart(
                    eventPage,
                    data.submitEventRegistration.quantity,
                    data.submitEventRegistration.id,
                    data.submitEventRegistration.userCode,
                );

                if (!!addToCartMutation) {
                    addToCartMutation.then((response: any) => {
                        if (response) {
                            if (isAlreadyInCart) {
                                navigate('/cart-summary');
                            } else {
                                setIsAddedToCartDialogOpen(true);
                            }
                        }
                    }).finally(() => {
                        setIsButtonLoading(false);
                    });
                } else {
                    setIsButtonLoading(false);
                }
            }).catch(() => setIsButtonLoading(false));
    }, [appState.cart, appState.ipCountry?.code, appState.countryCode?.code, eventPage, onSubmit, addToCart, isAlreadyInCart, navigate]);

    const onCloseAddedToCartDialog = () => {
        setIsAddedToCartDialogOpen(false);
    }

    const onClickEdit = useCallback((index: number) => {
        navigate(up(1));
        setIsEditing(true);
        setExpandedIds([index], collapseIdPrefix);
        setSelectedRegistrantIndex(index);
    }, [setSelectedRegistrantIndex, setIsEditing, setExpandedIds, navigate, collapseIdPrefix]);

    const closeCountryCheckoutPopup = useCallback(() => {
        setShowErrorPopup(false);
    }, []);

    const onCart = useCallback(() => {
        closeCountryCheckoutPopup();
        navigate('/cart-summary')
    }, [closeCountryCheckoutPopup, navigate]);

    if (!registrantsData) return null;

    const containerStyle = css`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        width: 1010px;

        ${theme.breakpoints.only('xxl')} {
            margin-left: 98px;
        }

        ${theme.breakpoints.only('xl')} {
            width: 1048px;
            margin-left: 44px;
        }

        ${theme.breakpoints.only('lg')} {
            width: 920px;
        }

        ${theme.breakpoints.down('md')} {
            width: 100%;
            justify-content: center;
        }
    `;

    const registrationStyle = css`
        font-weight: ${theme.fonts.weights.bold};
    `;

    const eventRegistrationStyle = css`
        justify-content: flex-start;
        margin-bottom: 26px;

        ${theme.breakpoints.up('3xl')} {
            margin-bottom: 25px;
        }

        ${theme.breakpoints.only('lg')} {
            margin-bottom: 36px;
        }

        ${theme.breakpoints.down('md')} {
            margin-bottom: 32px;
            justify-content: center;
        }

        ${theme.breakpoints.down('xs')} {
            margin-bottom: 36px;
        }
    `;

    const prefixContainerStyle = css`
        display: flex;
        justify-content: space-between;

        ${theme.breakpoints.only('md')} {
            margin-top: 45px;
        }

        ${theme.breakpoints.only('sm')} {
            margin-top: 36px;
        }

        ${theme.breakpoints.down('xs')} {
            margin-top: 50px;
        }
    `;

    const prefixStyle = css`
        font-weight: ${theme.fonts.weights.bold};
        padding-bottom: 8px;

        ${theme.breakpoints.down('md')} {
            padding-bottom: 14px;
        }
    `;

    const attendeeContainerStyle = ({ isFirst = false } = {}) => css`
        position: relative;
        width: 625px;
        padding: 0 19px;
        background-color: ${theme.colours.grey[100]};
        overflow: hidden;
        border-radius: 11px;

        ${theme.breakpoints.down('lg')} {
            width: 100%;
        }

        ${theme.breakpoints.only('sm')} {
            padding: 0 14px;
        }

        ${!isFirst && css` margin-top: 11px; `};
    `;

    const topAndBottomContainer = ({ isTop = false, isMainRegistrant = false } = {}) => css`
        height: 65px;
        display: flex;
        align-items: center;
        justify-content: space-between;

        ${isTop && css`
            border-bottom: 1.5px solid ${theme.colours.alto5};

            ${theme.breakpoints.down('xs')} {
                padding: 8px 0;
                flex-direction: column;
                align-items: flex-start;

                ${!isMainRegistrant && css` justify-content: center; `};
            }
        `};
    `;

    const attendeeNumberStyle = ({ isVisible = false, isGrey = false } = {}) => css`
        color: ${isGrey ? theme.colours.grey[700] : theme.colours.curiousBlue2};

        ${!isVisible && css` display: none; `};
    `;

    const detailsContainer = css`
        display: flex;
        flex-direction: row;
        padding: 14px 0 14px 6px;
        border-bottom: 1.5px solid ${theme.colours.alto5};

        ${theme.breakpoints.down('xs')} {
            flex-direction: column;
            padding: 11px 0 11px 0;
        }
    `;

    const detailName = css`
        width: 240px;
        font-size: 14px;
        line-height: 17px;
        padding-right: 20px;
        flex-shrink: 0;
        font-weight: ${theme.fonts.weights.bold};
        color: ${theme.colours.curiousBlue};

        ${theme.breakpoints.only('sm')} {
            width: 216px;
        }
    `;

    const checkboxMultipleValueContainerStyle = css`
        display: flex;
        flex-direction: column;
    `;

    const checkboxMultipleValueStyle = css`
        line-height: 24px;
    `;

    const editStyle = css`
        color: ${theme.colours.curiousBlue2};
        text-decoration: underline;
        cursor: pointer;
    `;

    const chevronStyle = ({ isUp = false } = {}) => css`
        position: absolute;
        right: 22px;
        bottom: 24px;
        width: 19px;
        height: 12px;
        fill: ${theme.colours.curiousBlue2};
        transform: rotateX(180deg);
        transition: transform 300ms ease;
        cursor: pointer;

        ${isUp && css`
            transform: rotateX(0);
            bottom: 27px;
        `};
    `;

    const acceptanceContainer = css`
        display: flex;
        flex-direction: column;
        margin-top: 20px;
        gap: 8px;

        ${theme.breakpoints.down('md')} {
            gap: 3px;
        }
    `;

    const termsAndConditionsStyle = css`
        font-size: 14px;
        line-height: 17px;
        font-weight: ${theme.fonts.weights.light};
        font-style: italic;
    `;

    const registrationFormStyle = css`
        margin-top: 20px;
        margin-bottom: 0;

        ${theme.breakpoints.down('md')} {
            margin-bottom: 0;
        }
    `;

    const formActionsContainerStyle = css`
        display: flex;
        flex: 1;
        margin-top: 50px;
        justify-content: flex-end;

        ${theme.breakpoints.only('lg')} {
            margin-top: 16px;
        }
    `;

    const eventDetailLargeBpsStyle = css`
        ${theme.breakpoints.down('md')} {
            display: none;
        }
    `;

    const eventDetailMobileBpsStyle = css`
        ${theme.breakpoints.up('lg')} {
            display: none;
        }

        ${theme.breakpoints.down('xs')} {
            .detail-container {
                width: 341px;
            }
        }
    `;

    const eventFormContainerStyle = css`
        ${theme.breakpoints.down('sm')} {
            padding: 0;
        }

        ${theme.breakpoints.only('sm')} {
            width: 525px;
        }

        ${theme.breakpoints.down('xs')} {
            width: 341px;
        }
    `;

    const detailValueContainerStyle = css`
        word-break: break-word;
    `;

    const countryConfirmationModalStyle = css`
        .confirmation-modal-content {
            margin: auto 0;
        }
    `;

    return (
        <>
            {isAddedToCartDialogOpen && (
                <AddedToCartDialog
                    productName={eventPage.title}
                    productImage={eventPage.imagesAndVideos?.[0]?.image}
                    numberOfItems={registrantsData.length}
                    totalPrice={registrantsData.length * eventPage.price.amount}
                    bottomButtonText={'Continue Shopping'}
                    onClickBottomButton={onCloseAddedToCartDialog}
                    onClose={onCloseAddedToCartDialog}
                />
            )}
            <div css={containerStyle}>
                <EventDetail
                    css={eventDetailLargeBpsStyle}
                    title={eventPage.title}
                    startDate={eventPage.eventDate}
                    endDate={eventPage.eventEndDate}
                    startTime={eventPage.eventStartTime}
                    endTime={eventPage.eventEndTime}
                    location={eventPage.location || eventPage.bannerLocationLabel}
                />
                <EventFormContainer css={eventFormContainerStyle}>
                    <ContentCombine fontSizeType={'25'} css={eventRegistrationStyle}>
                        Detail <span css={registrationStyle}>Confirmation</span>
                    </ContentCombine>
                    <EventDetail
                        css={eventDetailMobileBpsStyle}
                        title={eventPage.title}
                        startDate={eventPage.eventDate}
                        endDate={eventPage.eventEndDate}
                        startTime={eventPage.eventStartTime}
                        endTime={eventPage.eventEndTime}
                        location={eventPage.location || eventPage.bannerLocationLabel}
                    />
                    <div css={prefixContainerStyle}>
                        <ContentCombine
                            css={prefixStyle}
                            fontSizeType={'20'}
                        >
                            Attendee details
                        </ContentCombine>
                        <ContentCombine
                            fontSizeType={'20'}
                            css={prefixStyle}
                        >
                            {pluralize('ticket', registrantsData.length, true)}
                        </ContentCombine>
                    </div>
                    {registrantsData.map((r: RegistrantData, i: number) => (
                        <Collapse
                            isOpen={getIsIdExpanded(i, collapseIdPrefix)}
                            collapseHeight={'62px'}
                            css={attendeeContainerStyle({ isFirst: i === 0 })} key={i}
                        >
                            <div
                                css={topAndBottomContainer({
                                    isTop: true,
                                    isMainRegistrant: i === 0,
                                })}
                                ref={ref => containerRefs.current[collapseIdPrefix + i] = ref}
                            >
                                <ContentCombine fontSizeType={'20'}>
                                    {`${r.firstName} ${r.lastName}`}
                                </ContentCombine>
                                <div
                                    css={attendeeNumberStyle({
                                        isVisible: getIsIdExpanded(i, collapseIdPrefix) || breakpoint === 'xs',
                                        isGrey: i !== 0,
                                    })}
                                >
                                    Attendee {i + 1}
                                </div>
                            </div>
                            <div css={detailsContainer}>
                                <div css={detailName}>
                                    Email
                                </div>
                                <div css={detailValueContainerStyle}>
                                    {r.email}
                                </div>
                            </div>
                            {r.customFields.filter((cf: CustomField) => !cf.isOnConfirmationPage).map((cf: any) => (
                                <div css={detailsContainer} key={cf.id}>
                                    <div css={detailName}>
                                        {cf.label}
                                    </div>
                                    <div css={detailValueContainerStyle}>
                                        {cf.type.indexOf('checkbox') < 0 && <>{cf.value}</>}
                                        {cf.type === 'checkbox' && <>{parseInt(cf.value) ? 'Yes' : 'No'}</>}
                                        {cf.type === 'checkbox-multiple' && (
                                            <div css={checkboxMultipleValueContainerStyle}>
                                                {cf.optionsChosen.map((o: EventRegistrationOption) => (
                                                    <div css={checkboxMultipleValueStyle} key={o.id}>
                                                        {o.label}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <div css={topAndBottomContainer({ isTop: false })}>
                                <div
                                    css={editStyle}
                                    onClick={() => onClickEdit(i)}
                                >
                                    Edit
                                </div>
                            </div>
                            <Chevron
                                onClick={() => toggleExpandedId(
                                    i,
                                    collapseIdPrefix,
                                    { scrollRef: containerRefs.current[collapseIdPrefix + i] }
                                )
                                }
                                css={chevronStyle({
                                    isUp: getIsIdExpanded(i, collapseIdPrefix),
                                })}
                            />
                        </Collapse>
                    ))}
                    <Form onSubmit={onConfirm}>
                        <div css={acceptanceContainer}>
                            {eventPage.termsAndConditions && (
                                <>
                                    <div>Terms & Conditions</div>
                                    <div
                                        css={termsAndConditionsStyle}
                                        dangerouslySetInnerHTML={{ __html: eventPage.termsAndConditions }}
                                    />
                                </>
                            )}
                            {formFields.some((f: FormField) => f.isOnConfirmationPage) && (
                                <RegistrationForm
                                    css={registrationFormStyle}
                                    formFields={formFieldsForRender}
                                    fields={fields}
                                    errors={errors}
                                    onFieldChange={onFieldChange}
                                />
                            )}
                            <div css={formActionsContainerStyle}>
                                <EventRegistrationFormActions isDisabled={!isValid}>
                                    <FormButton type={"submit"} loading={isButtonLoading}>
                                        {isAlreadyInCart ? 'Update cart' : 'Confirm'}
                                    </FormButton>
                                </EventRegistrationFormActions>
                            </div>
                        </div>
                    </Form>
                </EventFormContainer>
            </div>
            {showErrorPopup && (
                <ConfirmationModal
                    message={
                        <>
                            <div>
                                Oops! &nbsp;This event doesn’t match your region.
                            </div>
                            <br/>
                            <div>
                                Please check out your cart first, then change country to browse events in your region.
                            </div>
                        </>
                    }
                    confirmLabel={"Go to cart"}
                    onCancel={closeCountryCheckoutPopup}
                    onConfirm={onCart}
                    css={countryConfirmationModalStyle}
                />
            )}
        </>
    );
}

export default DetailConfirmation;
